import { Suspense, useEffect, useState } from "react";
import { chakra, Box, Spinner, IconButton, Tooltip } from "@chakra-ui/react";
import { Outlet } from "react-router-dom";
import queryString from "query-string";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import Sidebar from "./sidebar";
import Mousetrap from "mousetrap";
import useIsMobile from "@ui/hooks/useIsMobile";
import { useACL } from "@ui/components/document/DocumentACL";

const Layout = () => {
  const [isHidden, setHidden] = useState<boolean>(
    localStorage.getItem("sidebar.isHidden") === "true",
  );
  const isMobile = useIsMobile();
  const isOverlay = !isHidden && (isMobile ?? false);

  const handleCollapse = () => {
    if (isHidden) {
      setHidden(false);
    } else {
      setHidden(true);
    }
  };

  useEffect(() => {
    if (isHidden === undefined) return;
    localStorage.setItem("sidebar.isHidden", isHidden.toString());
  }, [isHidden]);

  useEffect(() => {
    const handleToggleSidebar = () => {
      handleCollapse();
    };
    Mousetrap.bind("[", handleToggleSidebar);

    return () => {
      Mousetrap.unbind("[");
    };
  }, [handleCollapse]);

  const { isPermitted } = useACL();
  const isSuperAdmin = isPermitted(["group:super-admin"], undefined, undefined);
  const isEmployeeAdmin = isPermitted(
    ["group:employee-admin"],
    undefined,
    undefined,
  );
  const isAdmin = isPermitted(["group:admin"], undefined, undefined);
  const isGuestBookingAdmin = isPermitted(
    ["group:guest-booking-admin"],
    undefined,
    undefined,
  );

  return (
    <QueryParamProvider
      adapter={ReactRouter6Adapter}
      options={{
        searchStringToObject: queryString.parse,
        objectToSearchString: queryString.stringify,
      }}
    >
      <Box height="100dvh" display="flex" overflow="hidden">
        {!isMobile &&
          (isSuperAdmin ||
            isEmployeeAdmin ||
            isAdmin ||
            isGuestBookingAdmin) && (
            <Box
              flex="0 0 auto"
              zIndex={99}
              h="full"
              w={
                isOverlay
                  ? isHidden
                    ? "20px"
                    : "100vw"
                  : isHidden
                    ? "20px"
                    : "280px"
              }
              position={isOverlay ? "absolute" : "relative"}
              top={0}
              left={0}
              bg={isMobile ? "transparent" : "neutrals.100"}
              borderRight={"1px solid #DCDFE4"}
            >
              <Box
                h="full"
                w={isHidden ? "20px" : isOverlay ? "100vw" : "280px"}
                position="absolute"
                borderRight={!isMobile ? "1px solid #DCDFE4" : undefined}
                transition="width 0.2s ease"
                visibility={isHidden ? "hidden" : "visible"}
                opacity={isHidden ? 0 : 1}
                bg="white"
              >
                <Sidebar
                  handleCollapse={handleCollapse}
                  isOverlay={isOverlay}
                />
              </Box>
              {(!isOverlay || isHidden) && (
                <Tooltip
                  label={isHidden ? "Expand [" : "Collapse ["}
                  placement="auto"
                  background="#44546f"
                  fontSize="12px"
                >
                  <IconButton
                    position="absolute"
                    top={10}
                    left={
                      isHidden
                        ? "8px"
                        : isOverlay
                          ? "calc(100vw - 12px - 24px)"
                          : "268px"
                    }
                    w={6}
                    h={6}
                    zIndex={100}
                    onClick={handleCollapse}
                    background="neutrals.0"
                    borderRadius="full"
                    variant="outline"
                    role="group"
                    _hover={{ background: "background.blue.default" }}
                    icon={
                      <chakra.span
                        color="#172B4D"
                        _groupHover={{ color: "white" }}
                      >
                        {isHidden ? (
                          <ChevronRight strokeWidth={4} size="10px" />
                        ) : (
                          <ChevronLeft strokeWidth={4} size="10px" />
                        )}
                      </chakra.span>
                    }
                    aria-label=""
                  />
                </Tooltip>
              )}
            </Box>
          )}
        <Box transition={"all 0.2s ease"} w="full" h="full" overflow={"hidden"}>
          <Suspense
            fallback={
              <Box
                w="full"
                h="full"
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <Spinner size="lg" />
              </Box>
            }
          >
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </QueryParamProvider>
  );
};

export default Layout;
